import React, { useState, useEffect, useCallback, useMemo } from "react";
import tinycolor from "tinycolor2";
import { useDispatch, useSelector } from "../redux/store";
import { selectShops } from "../redux/selectors";
import { updateAppointment } from "../redux/slices/appointmentSlice";
import { sendToWindowDataLayer } from "../utils/dom";
import {
  buildBailoutPayload,
  captureBailout,
  hasCallbackNumber,
} from "../utils/bailout";
import { compileTemplate } from "../utils/formatting";
import { getAppointmentTheme } from "../utils/theme";
import {
  ErrorMessage,
  Field,
  Form,
  SimpleForm,
} from "../components/SimpleForm";

// We'll build a validation schema inside the component so it can be conditional

const StayOrGo = ({ appointment }) => {
  const [showThanks, setShowThanks] = useState(false);
  const [bailOutValues, setBailOutValues] = useState({});
  const [bailMessage, setBailMessage] = useState(
    "If you change your mind, we're here to help!"
  );

  const dispatch = useDispatch();
  const shops = useSelector(selectShops) || [];
  const initialShopIdRef = React.useRef(appointment?.shopData?.shopId || "");

  const { accentColor, gradientBackground } = useMemo(
    () => getAppointmentTheme(appointment),
    [appointment]
  );
  const encodedAccentColor = useMemo(
    () => (accentColor || "#2b73b9").replace("#", "%23"),
    [accentColor]
  );

  const buildHandlebars = useCallback(() => {
    const result = compileTemplate(
      appointment?.shopData?.messages?.bailMessage ??
        "If you change your mind, we're here to help!",
      {
        your_service: appointment?.service,
        on_date: appointment?.date,
        at_time: appointment?.time,
      }
    );
    setBailMessage(result);
  }, [appointment]);

  useEffect(() => {
    buildHandlebars();
  }, [buildHandlebars]);

  const bailout = (values) => {
    const selectedShopId = values.siteId || appointment.shopData?.shopId;
    const rawSelectedShop =
      shops.find((s) => (s._id || s.shopId) === selectedShopId) ||
      appointment.shopData;
    const selectedShop = rawSelectedShop
      ? { ...(rawSelectedShop || {}), shopId: selectedShopId }
      : { shopId: selectedShopId };

    const payload = buildBailoutPayload({
      appointment,
      customer: {
        fname: values.fname,
        lname: values.lname,
        phone: values.phone,
      },
      comments: values.comments,
      bailOutStep: appointment.previousStage,
      shopId: selectedShopId,
      shopData: selectedShop,
    });

    captureBailout({
      appointment,
      payload,
      dispatch,
      updateAppointmentAction: updateAppointment,
    })
      .then(() => {
        dispatch(
          updateAppointment({
            updates: {
              bailed: true,
            },
          })
        );
        if (appointment?.customer?.phone) {
          sendToWindowDataLayer(
            "dc-service-partial_conversion",
            "dc-service-partial_conversion"
          );
        }
        sendToWindowDataLayer("dc-service-bailed", "dc-service-bailed");
        setBailOutValues(payload);
        setShowThanks(true);
      })
      .catch((error) => {
        console.error("Error in bailout process:", error);
      });
  };

  // Keep bailout form inputs reflected in appointment state so Header bailout
  // capture can include them if the user exits without submitting the form.
  const handleFieldChange = (e, setFieldValue) => {
    const { name, value } = e.target;
    setFieldValue(name, value);
    if (["fname", "lname", "phone"].includes(name)) {
      dispatch(
        updateAppointment({
          updates: {
            customer: {
              ...appointment?.customer,
              [name]: value,
            },
          },
        })
      );
    } else if (name === "comments") {
      dispatch(
        updateAppointment({
          updates: {
            bailOutStage: {
              bailOutStep: appointment.previousStage,
              bailOutComments: value,
            },
          },
        })
      );
    }
  };

  const AreYouSure = () => {
    return (
      <div
        className="mb-5 p-4 rounded-xl text-sm"
        style={{
          backgroundColor: tinycolor(accentColor).setAlpha(0.15).toRgbString(),
          color: "#1f2937",
        }}
      >
        {bailMessage}
      </div>
    );
  };

  return (
    <div
      className="px-4 py-5 md:px-6 md:py-6 min-h-full font-roboto"
      style={{ background: gradientBackground }}
    >
      <div
        className="rounded-2xl bg-white p-5"
        style={{
          border: `2px solid ${accentColor}33`,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        {showThanks && appointment.stage > 0 ? (
          <div className="text-sm" style={{ whiteSpace: "pre-line", color: "#1f2937" }}>
            {appointment?.shopData?.bailOutThanks ?? (
              <>
                Thanks for visiting,{" "}
                {bailOutValues?.customer?.fname?.charAt(0).toUpperCase() +
                  bailOutValues?.customer?.fname?.slice(1)}
                ! We will reach out to you shortly to discuss your service
                needs. In the meantime, feel free to contact us at{" "}
                <a
                  href={`tel:${appointment?.shopData?.shopContact?.phone?.number}`}
                  style={{ color: accentColor }}
                >
                  {appointment?.shopData?.shopContact?.phone?.number}
                </a>
                <br />
                <br />
                <em>- Your {appointment.shopData?.shopName?.name} Team</em>
              </>
            )}
          </div>
        ) : (
          <SimpleForm
            initialValues={{
              siteId: appointment?.shopData?.shopId || "",
              fname: appointment?.customer?.fname || "",
              lname: appointment?.customer?.lname || "",
              phone: appointment?.customer?.phone || "",
              comments: "",
            }}
            validate={(values) => {
              const required = (value) => (value ? undefined : "(required)");
              return {
                siteId: appointment?.shopData?.shopId
                  ? undefined
                  : required(values.siteId),
                fname: required(values.fname),
                lname: required(values.lname),
                phone:
                  required(values.phone) ??
                  (hasCallbackNumber(values.phone) ? undefined : "(invalid)"),
              };
            }}
            onSubmit={(values) => {
              bailout(values);
            }}
          >
            {({ touched, errors, handleSubmit, setFieldValue, values }) => (
              <Form>
                <h2
                  className="text-lg font-bold mb-4"
                  style={{ color: "#1f2937" }}
                >
                  What'd we miss?
                </h2>

                <AreYouSure />

                {/* Show site selector only when the appointment didn't already have a shopId on mount */}
                {!initialShopIdRef.current && (
                  <div className="mb-4">
                    <label
                      htmlFor="siteId"
                      className="block mb-1 text-sm font-semibold"
                      style={{ color: "#1f2937" }}
                    >
                      Select a location
                    </label>
                    <select
                      name="siteId"
                      data-testid="bailout-site-select"
                      aria-required="true"
                      className="w-full box-border p-2 pr-12 border-2 rounded-lg text-sm appearance-none bg-no-repeat"
                      style={{
                        border: `2px solid ${accentColor}66`,
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none'%3E%3Cpath d='M5 7l5 6 5-6' stroke='${encodedAccentColor}' stroke-width='2.75' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                        backgroundPosition: "right 14px center",
                        backgroundSize: "16px 16px",
                      }}
                      value={values.siteId}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFieldValue("siteId", val);
                        // Update appointment shopData/shopId in redux so header and other parts reflect selection
                        const sel =
                          shops.find((s) => (s._id || s.shopId) === val) ||
                          appointment.shopData ||
                          {};
                        const normalized = { ...sel, shopId: val };
                        dispatch(
                          updateAppointment({
                            updates: {
                              shopData: normalized,
                              shopId: val,
                            },
                          })
                        );
                      }}
                    >
                      <option value="">-- Select location --</option>
                      {shops.map((s) => (
                        <option
                          key={s._id || s.shopId}
                          value={s._id || s.shopId}
                        >
                          {s.shopName?.name ||
                            s.name ||
                            s.displayName ||
                            s.shopName}
                        </option>
                      ))}
                    </select>
                    <ErrorMessage
                      name="siteId"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                )}

                <div className="flex flex-col md:grid md:grid-cols-2 gap-4 md:gap-6">
                  {/* Left column: text fields */}
                  <div className="flex min-w-0 flex-col gap-4">
                    <div className="flex flex-col">
                      <label
                        htmlFor="fname"
                        className="text-sm font-semibold mb-1"
                        style={{ color: "#1f2937" }}
                      >
                        First Name{" "}
                        <ErrorMessage
                          name="fname"
                          component="span"
                          className="text-red-500 text-sm font-normal"
                        />
                      </label>
                      <Field
                        name="fname"
                        type="text"
                        className="w-full box-border p-2 border-2 rounded-lg text-sm"
                        style={{
                          border: `2px solid ${accentColor}66`,
                        }}
                        onChange={(e) => handleFieldChange(e, setFieldValue)}
                        data-testid="bailout-fname"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label
                        htmlFor="lname"
                        className="text-sm font-semibold mb-1"
                        style={{ color: "#1f2937" }}
                      >
                        Last Name{" "}
                        <ErrorMessage
                          name="lname"
                          component="span"
                          className="text-red-500 text-sm font-normal"
                        />
                      </label>
                      <Field
                        name="lname"
                        type="text"
                        className="w-full box-border p-2 border-2 rounded-lg text-sm"
                        style={{
                          border: `2px solid ${accentColor}66`,
                        }}
                        onChange={(e) => handleFieldChange(e, setFieldValue)}
                        data-testid="bailout-lname"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label
                        htmlFor="phone"
                        className="text-sm font-semibold mb-1"
                        style={{ color: "#1f2937" }}
                      >
                        Phone Number{" "}
                        <ErrorMessage
                          name="phone"
                          component="span"
                          className="text-red-500 text-sm font-normal"
                        />
                      </label>
                      <Field
                        name="phone"
                        type="text"
                        className="w-full box-border p-2 border-2 rounded-lg text-sm"
                        style={{
                          border: `2px solid ${accentColor}66`,
                        }}
                        onChange={(e) => handleFieldChange(e, setFieldValue)}
                        data-testid="bailout-phone"
                      />
                    </div>
                  </div>

                  {/* Right column: textarea */}
                  <div className="flex min-w-0 flex-col">
                    <label
                      htmlFor="comments"
                      className="text-sm font-semibold mb-1"
                      style={{ color: "#1f2937" }}
                    >
                      How can we help?{" "}
                      <ErrorMessage
                        name="comments"
                        component="span"
                        className="text-red-500 text-sm font-normal"
                      />
                    </label>
                    <Field
                      name="comments"
                      as="textarea"
                      rows="4"
                      className="w-full box-border p-2 border-2 rounded-lg text-sm md:h-full min-h-[120px]"
                      style={{
                        border: `2px solid ${accentColor}66`,
                      }}
                      onChange={(e) => handleFieldChange(e, setFieldValue)}
                      data-testid="bailout-comments"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 mt-6">
                  <button
                    type="button"
                    className="cursor-pointer rounded-lg border-2 border-solid px-5 py-2 text-sm font-semibold transition-opacity hover:opacity-80 active:opacity-70 focus:outline-none"
                    style={{
                      backgroundColor: "#ffffff",
                      borderColor: accentColor,
                      color: accentColor,
                      boxShadow: "none",
                    }}
                    onClick={() => {
                      dispatch(
                        updateAppointment({
                          updates: {
                            stage: appointment.previousStage,
                          },
                        })
                      );
                    }}
                  >
                    Continue Booking
                  </button>
                  <button
                    type="button"
                    className="cursor-pointer rounded-lg px-5 py-2 text-sm font-semibold transition-opacity hover:opacity-90 active:opacity-80 focus:outline-none"
                    style={{
                      backgroundColor: accentColor,
                      color: "#ffffff",
                      border: "none",
                      boxShadow: "none",
                    }}
                    onClick={handleSubmit}
                    data-testid="bailout-submit"
                  >
                    Request Help
                  </button>
                </div>
              </Form>
            )}
          </SimpleForm>
        )}
      </div>
    </div>
  );
};

export { StayOrGo };
